import React, { useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input as ShadcnInput } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { CustomCsvImportCapabilities } from "../../../services/brokerIntegrationApi";
import type { CustomCsvWizardState } from "../../../types/customCsvImport";
import type { TradeAccountOption } from "../../../contexts/ActiveBrokerAccountContext";
import CsvImportAccountCodeField from "../CsvImportAccountCodeField";
import { invalidatePreviewAfterConfigChange } from "../../../utils/customCsvImport";
import { TimezoneCombobox } from "@/components/TimezoneCombobox";
import {
  CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS,
  CUSTOM_CSV_FIELD_CARD_CLASS,
  CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS,
  CUSTOM_CSV_SELECT_CONTENT_PROPS,
} from "./customCsvWizardUi";

export interface CustomCsvMetadataStepProps {
  csvImport: CustomCsvImportCapabilities;
  state: CustomCsvWizardState;
  brokerAccounts: TradeAccountOption[];
  onStateChange: (patch: Partial<CustomCsvWizardState>) => void;
  /** Demo Entry Analysis — skip account chrome (no persist). */
  demoMode?: boolean;
}

function MetadataFieldCard({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Card
      data-custom-csv-metadata-card=""
      className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
    >
      <CardContent className={CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS}>{children}</CardContent>
    </Card>
  );
}

const CustomCsvMetadataStep = ({
  csvImport,
  state,
  brokerAccounts,
  onStateChange,
  demoMode = false,
}: CustomCsvMetadataStepProps): React.ReactElement => {
  const quantityOptions = csvImport.quantity_unit_options ?? ["units", "lots"];
  const contractEntries = useMemo(
    () => Object.entries(state.contractMultipliers),
    [state.contractMultipliers]
  );

  const patchWithPreviewReset = (patch: Partial<CustomCsvWizardState>) => {
    onStateChange({
      ...patch,
      preview: invalidatePreviewAfterConfigChange(state.preview),
      acknowledgedWarningCodes: new Set(),
    });
  };

  const setContractMultiplier = (symbol: string, value: number | undefined) => {
    const next = { ...state.contractMultipliers };
    if (value == null || !Number.isFinite(value) || value <= 0) {
      delete next[symbol];
    } else {
      next[symbol] = value;
    }
    patchWithPreviewReset({ contractMultipliers: next });
  };

  const addContractMultiplierRow = () => {
    let index = 1;
    let symbol = "SYMBOL";
    while (state.contractMultipliers[symbol] != null) {
      index += 1;
      symbol = `SYMBOL_${index}`;
    }
    patchWithPreviewReset({
      contractMultipliers: { ...state.contractMultipliers, [symbol]: 1 },
    });
  };

  const renameContractMultiplier = (oldSymbol: string, newSymbol: string) => {
    const trimmed = newSymbol.trim().toUpperCase();
    if (!trimmed || trimmed === oldSymbol) return;
    const value = state.contractMultipliers[oldSymbol];
    if (value == null) return;
    const next = { ...state.contractMultipliers };
    delete next[oldSymbol];
    next[trimmed] = value;
    patchWithPreviewReset({ contractMultipliers: next });
  };

  const removeContractMultiplier = (symbol: string) => {
    const next = { ...state.contractMultipliers };
    delete next[symbol];
    patchWithPreviewReset({ contractMultipliers: next });
  };

  return (
    <div className="flex flex-col gap-5" data-custom-csv-metadata-step="">
      {!demoMode ? (
        <>
          <MetadataFieldCard>
            <Field className="gap-2" data-broker-csv-field="">
              <FieldLabel htmlFor="custom-csv-display-name" className={BROKER_ALERT_TITLE_CLASS}>
                Executing broker / prop firm
                <span className="text-destructive"> *</span>
              </FieldLabel>
              <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                The real broker this account represents, not shown as a Tessera integration lane.
              </FieldDescription>
              <ShadcnInput
                id="custom-csv-display-name"
                placeholder="e.g. FTMO, IC Markets"
                value={state.displayName}
                onChange={(e) => onStateChange({ displayName: e.target.value })}
                className="font-[family-name:var(--font-primary)]"
              />
            </Field>
          </MetadataFieldCard>

          <MetadataFieldCard>
            <CsvImportAccountCodeField
              field="account_name"
              label="Account name"
              hint="Labels this import in Tessera. Re-importing the same name updates trades."
              placeholder="e.g. Challenge 100k"
              value={state.accountName}
              onChange={(v) => onStateChange({ accountName: v })}
              brokerAccounts={brokerAccounts}
              preferNewAccountByDefault
            />
          </MetadataFieldCard>
        </>
      ) : null}

      <MetadataFieldCard>
        <Field className="gap-2" data-broker-csv-field="">
          <FieldLabel htmlFor="custom-csv-timezone" className={BROKER_ALERT_TITLE_CLASS}>
            Statement timezone
            <span className="text-destructive"> *</span>
          </FieldLabel>
          <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
            Pick the timezone your CSV timestamps are written in. Type in the menu to filter by
            city (options include the current UTC offset).
          </FieldDescription>
          <TimezoneCombobox
            id="custom-csv-timezone"
            value={state.timeZone}
            onValueChange={(value) => patchWithPreviewReset({ timeZone: value })}
            placeholder="Select timezone"
            triggerClassName={CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS}
            aria-label="Statement timezone"
          />
        </Field>
      </MetadataFieldCard>

      <MetadataFieldCard>
        <Field className="gap-2" data-broker-csv-field="">
          <FieldLabel htmlFor="custom-csv-statement-currency" className={BROKER_ALERT_TITLE_CLASS}>
            Statement account currency
          </FieldLabel>
          <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
            Your account currency for P&L checks on FX crosses (not used for live FX rates).
            Helps when mapping conversion_rate instead of broker P&L.
          </FieldDescription>
          <ShadcnInput
            id="custom-csv-statement-currency"
            placeholder="e.g. USD"
            maxLength={3}
            value={state.statementAccountCurrency}
            onChange={(e) =>
              patchWithPreviewReset({
                statementAccountCurrency: e.target.value.toUpperCase().replace(/[^A-Z]/g, ""),
              })
            }
            className="font-[family-name:var(--font-primary)] uppercase"
          />
        </Field>
      </MetadataFieldCard>

      <MetadataFieldCard>
        <Field className="gap-2" data-broker-csv-field="">
          <FieldLabel htmlFor="custom-csv-quantity-unit" className={BROKER_ALERT_TITLE_CLASS}>
            Quantity unit
          </FieldLabel>
          <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
            Use lots when your CSV reports lot size for CFD or FX symbols.
          </FieldDescription>
          <Select
            value={state.quantityUnit}
            onValueChange={(value) =>
              patchWithPreviewReset({
                quantityUnit: value as "units" | "lots",
              })
            }
          >
            <SelectTrigger id="custom-csv-quantity-unit" className={CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS}>
              <SelectValue placeholder="Select quantity unit" />
            </SelectTrigger>
            <SelectContent {...CUSTOM_CSV_SELECT_CONTENT_PROPS}>
              {quantityOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </MetadataFieldCard>

      <MetadataFieldCard>
        <Field className="gap-2" data-broker-csv-field="">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
              Contract multipliers (optional)
            </FieldLabel>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              className="h-8 shrink-0 px-4 text-[0.75rem] font-normal normal-case tracking-normal"
              onClick={addContractMultiplierRow}
            >
              <Plus className="size-4 text-[color:var(--brand-tropical-surf)]" aria-hidden />
              Add symbol
            </Button>
          </div>
          <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
            Override contract size or point value per symbol when broker P&L is missing, e.g.
            SPX500 → 10 for index CFDs.
          </FieldDescription>
          {contractEntries.length === 0 ? (
            <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
              None set. Add a symbol if preview warns about missing contract specs.
            </p>
          ) : (
            <div className="flex flex-col gap-2 pt-1">
              {contractEntries.map(([symbol, multiplier]) => (
                <div key={symbol} className="flex flex-wrap items-start gap-2">
                  <ShadcnInput
                    aria-label={`Symbol for ${symbol}`}
                    placeholder="Symbol"
                    value={symbol}
                    onChange={(e) => renameContractMultiplier(symbol, e.target.value)}
                    className="min-w-[8rem] flex-1 font-[family-name:var(--font-primary)] uppercase"
                  />
                  <ShadcnInput
                    aria-label={`Multiplier for ${symbol}`}
                    type="number"
                    min={0}
                    step="any"
                    value={Number.isFinite(multiplier) ? multiplier : ""}
                    onChange={(e) => {
                      const parsed = Number.parseFloat(e.target.value);
                      setContractMultiplier(
                        symbol,
                        e.target.value === "" ? undefined : parsed
                      );
                    }}
                    className="w-[7.5rem] shrink-0 font-[family-name:var(--font-primary)]"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label={`Remove ${symbol}`}
                    className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => removeContractMultiplier(symbol)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Field>
      </MetadataFieldCard>
    </div>
  );
};

export default CustomCsvMetadataStep;
